/**
 * Routine Summary Block Renderer
 *
 * The `ui_routine_summary` card: the final review before creation — title,
 * description, alert logic, schedule, and delivery rows, with Cancel / Create
 * buttons. The button choice returns as the tool output (`{ action }`); after
 * acting (or when rehydrated via `resolvedAction`) the buttons collapse into
 * a settled state line.
 */

import React from "react";
import { AlarmClock, Check, Clock, X } from "lucide-react";
import { Button } from "@strivelabs/ui-core/v2";
import {
  ROUTINE_CHANNEL_LABELS,
  RoutineChannelIcon,
  normalizeDeliveryRows,
} from "@/components/routines/routineChannelDisplay";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

export interface RoutineSummaryBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled (historical render). */
  readOnly?: boolean;
}

export function RoutineSummaryBlockRenderer({
  block,
  onUIAction,
  readOnly = false,
}: RoutineSummaryBlockRendererProps) {
  const content = block.type === "routine_summary" ? block.content : null;
  const [resolved, setResolved] = React.useState<"create" | "cancel" | null>(
    content?.resolvedAction ?? null
  );

  React.useEffect(() => {
    setResolved(content?.resolvedAction ?? null);
  }, [content?.title, content?.resolvedAction]);

  if (block.type !== "routine_summary" || !content) return null;

  const deliveryRows = normalizeDeliveryRows(content.delivers_to);
  const disabled = readOnly || resolved !== null;

  const handleAction = (action: "create" | "cancel") => {
    if (disabled) return;
    setResolved(action);
    onUIAction({
      type: "ui_action",
      component: "routine_summary",
      label: content.title,
      state: { action },
    });
  };

  return (
    <div className="my-2 w-full rounded-lg border border-custom-border bg-[#fbfbfb]">
      <div className="flex items-start gap-3 p-5">
        <AlarmClock className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-base font-semibold text-gray-900">{content.title}</h3>
          <p className="text-sm text-gray-500">{content.description}</p>
        </div>
      </div>

      <div className="border-t border-custom-border p-5">
        <p className="text-sm font-medium text-gray-500">Alert logic</p>
        <p className="mt-1.5 text-sm text-gray-900">{content.alert_logic}</p>
      </div>

      <div className="border-t border-custom-border p-5">
        <p className="text-sm font-medium text-gray-500">Runs</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-900">
          <Clock className="h-4 w-4 text-gray-500" aria-hidden="true" />
          {content.schedule_label}
        </p>
      </div>

      {deliveryRows.length > 0 && (
        <div className="border-t border-custom-border p-5">
          <p className="text-sm font-medium text-gray-500">Delivers to</p>
          <div className="mt-1.5 flex flex-col gap-1.5">
            {deliveryRows.map((row, index) => (
              <div key={`${row.channel}-${index}`} className="flex items-center gap-1.5 text-sm">
                <RoutineChannelIcon channel={row.channel} />
                <span className="font-medium text-gray-900">
                  {ROUTINE_CHANNEL_LABELS[row.channel]}
                </span>
                {row.detail ? <span className="text-gray-500">· {row.detail}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-custom-border p-4">
        {resolved ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
            {resolved === "create" ? (
              <>
                <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                Routine creation confirmed.
              </>
            ) : (
              <>
                <X className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                Cancelled — tell me what to change.
              </>
            )}
          </p>
        ) : (
          <>
            <Button variant="secondary" size="sm"
              disabled={readOnly}
              onClick={() => handleAction("cancel")}
            >
              {content.cancel_label ?? "Cancel"}
            </Button>
            <Button size="sm"
              disabled={readOnly}
              onClick={() => handleAction("create")}
            >
              {content.confirm_label ?? "Create"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
